import {
  Dumbbell,
  GraduationCap,
  Smartphone,
  School,
  Briefcase,
  UtensilsCrossed,
  ShoppingCart,
  User,
} from "lucide-react";

const CATEGORY = {
  Gym: { icon: Dumbbell, color: "#fb923c" },
  Coaching: { icon: GraduationCap, color: "#22d3ee" },
  "Mobile Shop": { icon: Smartphone, color: "#34d399" },
  College: { icon: School, color: "#a78bfa" },
  Business: { icon: Briefcase, color: "#60a5fa" },
  Restaurant: { icon: UtensilsCrossed, color: "#fb7185" },
  "E-commerce": { icon: ShoppingCart, color: "#facc15" },
  Portfolio: { icon: User, color: "#818cf8" },
};

/*
  A crafted CSS "website mockup" — browser chrome + wireframe preview
  tinted with the category colour. `large` renders the modal version.
*/
export default function Mockup({ title, url, variant = "tiles", large = false }) {
  const meta = CATEGORY[title] || { icon: Briefcase, color: "#818cf8" };
  const Icon = meta.icon;
  const c = meta.color;
  const tiles = variant === "tiles" ? (large ? 4 : 3) : 0;
  const rows = variant === "rows" ? (large ? 4 : 3) : 0;

  return (
    <div
      className="relative h-full w-full overflow-hidden rounded-xl border border-white/10 bg-[#0a0c16]"
      data-testid="demo-mockup"
    >
      {/* glow wash */}
      <div
        className="pointer-events-none absolute -top-1/2 left-1/2 h-full w-[130%] -translate-x-1/2 rounded-full opacity-25 blur-3xl"
        style={{ background: `radial-gradient(circle, ${c}, transparent 65%)` }}
        aria-hidden="true"
      />
      {/* browser chrome */}
      <div className="relative flex items-center gap-2 border-b border-white/[0.07] bg-white/[0.03] px-3 py-2">
        <span className="flex gap-1.5">
          <span className="h-2 w-2 rounded-full bg-[#ff5f57]" />
          <span className="h-2 w-2 rounded-full bg-[#febc2e]" />
          <span className="h-2 w-2 rounded-full bg-[#28c840]" />
        </span>
        <span className="ml-1 flex-1 truncate rounded-md bg-white/[0.05] px-2.5 py-1 font-mono text-[9px] text-slate-500">
          {url}
        </span>
      </div>
      {/* wireframe body */}
      <div className="relative p-4">
        {/* site nav */}
        <div className="mb-3.5 flex items-center justify-between">
          <div className="flex items-center gap-1.5">
            <span className="h-3.5 w-3.5 rounded" style={{ background: c, opacity: 0.9 }} />
            <span className="h-2 w-14 rounded-full bg-white/25" />
          </div>
          <div className="flex gap-2">
            <span className="h-1.5 w-7 rounded-full bg-white/15" />
            <span className="h-1.5 w-7 rounded-full bg-white/15" />
            <span className="h-1.5 w-7 rounded-full bg-white/15" />
          </div>
        </div>
        {/* hero */}
        <div
          className="relative overflow-hidden rounded-lg border p-4"
          style={{
            borderColor: `${c}40`,
            background: `linear-gradient(140deg, ${c}26, transparent 60%)`,
          }}
        >
          <Icon className="absolute right-3 top-3 h-8 w-8" style={{ color: c, opacity: 0.5 }} />
          <span className="block h-2.5 w-3/5 rounded-full bg-white/50" />
          <span className="mt-2 block h-2.5 w-2/5 rounded-full bg-white/25" />
          <span
            className="mt-3 inline-block rounded-full px-3 py-1"
            style={{ background: c }}
          >
            <span className="block h-1.5 w-12 rounded-full bg-black/40" />
          </span>
        </div>
        {/* content tiles / rows */}
        {tiles > 0 && (
          <div className={`mt-3.5 grid gap-2.5 ${tiles === 4 ? "grid-cols-4" : "grid-cols-3"}`}>
            {Array.from({ length: tiles }).map((_, i) => (
              <div key={i} className="rounded-lg border border-white/[0.07] bg-white/[0.04] p-2.5">
                <span className="block h-6 w-6 rounded-md" style={{ background: `${c}55` }} />
                <span className="mt-2 block h-1.5 w-4/5 rounded-full bg-white/20" />
                <span className="mt-1.5 block h-1.5 w-3/5 rounded-full bg-white/10" />
              </div>
            ))}
          </div>
        )}
        {rows > 0 && (
          <div className="mt-3.5 space-y-2.5">
            {Array.from({ length: rows }).map((_, i) => (
              <div
                key={i}
                className="flex items-center gap-3 rounded-lg border border-white/[0.07] bg-white/[0.04] p-2.5"
              >
                <span className="h-6 w-6 shrink-0 rounded-md" style={{ background: `${c}55` }} />
                <div className="flex-1">
                  <span className="block h-1.5 w-2/5 rounded-full bg-white/25" />
                  <span className="mt-1.5 block h-1.5 w-3/5 rounded-full bg-white/10" />
                </div>
                <span className="h-1.5 w-8 rounded-full" style={{ background: `${c}88` }} />
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
